/**
 * Intranet Osasco Plaza — src/modules/recrutamento.jsx
 * Telas dos módulos "Cadastrar Vaga", "Consultar Currículos" e
 * "Candidatar a Vaga" (Fase 5 — Recrutamento). Extraído de app.jsx — ver
 * DOC/ADR/0011.
 */

import { Blocked, CARGOS_CURRICULO, CARGOS_VAGA, CheckboxGroup, ESCOLARIDADES, ESTADOS_BR, ESTADO_CIVIL, Icon, STATUS_CURRICULO, hasPerm, maskCep, maskCpf, maskPhone, validarCPF } from './shared.jsx';
const { useState, useEffect, useRef } = React;


export function CadastrarVagaScreen({ user, onChange }) {
  if (!hasPerm(user,'vagas','view')) return <Blocked/>;

  // Stepper steps
  const STEPS = [
    { label:'Cargo & Salário' },
    { label:'Contato' },
    { label:'Prazo & Detalhes' },
    { label:'Revisão' },
  ];
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    cargo:'', salario:'', complemento:'', responsavel:'', telefone:'', email:'', instagram:'', facebook:'', disponivelAte:'', detalhes:''
  });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [erroForm, setErroForm] = useState('');
  const [vagas, setVagas] = useState([]);
  const [vagasLoading, setVagasLoading] = useState(true);
  const [tab, setTab] = useState('nova'); // 'nova' | 'minhas'

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const carregarVagas = async () => {
    setVagasLoading(true);
    try {
      const r = await API.vagas.listar();
      setVagas(r.vagas || []);
    } catch (e) { /* a tabela fica vazia; a tela de baixo já cobre o caso */ }
    setVagasLoading(false);
  };
  useEffect(() => { carregarVagas(); }, [ok]);

  const submit = async () => {
    if (!form.cargo) return;
    setBusy(true); setErroForm('');
    try {
      await API.vagas.criar(form);
      onChange && onChange();
      setOk(true);
      setStep(0);
      setForm({ cargo:'', salario:'', complemento:'', responsavel:'', telefone:'', email:'', instagram:'', facebook:'', disponivelAte:'', detalhes:'' });
      setTimeout(() => { setOk(false); setTab('minhas'); }, 1800);
    } catch (e) {
      setErroForm(e.message || 'Não foi possível publicar a vaga.');
    }
    setBusy(false);
  };

  const desativar = async (id) => {
    try { await API.vagas.ativar(id); onChange && onChange(); await carregarVagas(); }
    catch (e) { alert(e.message || 'Não foi possível alterar a vaga.'); }
  };

  const excluir = async (id) => {
    if (!confirm('Excluir esta vaga?')) return;
    try { await API.vagas.remover(id); onChange && onChange(); await carregarVagas(); }
    catch (e) { alert(e.message || 'Não foi possível excluir a vaga.'); }
  };

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title">
          <h2>Painel de Empregos</h2>
          <p>Cadastre e gerencie vagas disponíveis nas lojas do shopping</p>
        </div>
      </div>

      {/* Aviso lojista */}
      <div style={{ background:'#fff3cd',border:'1px solid #ffc107',borderRadius:8,padding:'10px 16px',fontSize:12,color:'#856404',marginBottom:18,textAlign:'center',fontWeight:500 }}>
        🏪 Prezado Lojista, este cadastro é exclusivo para anúncio de vagas para sua loja no Shopping Osasco Plaza.
      </div>

      {/* Tabs */}
      <div style={{ display:'flex',gap:8,marginBottom:20 }}>
        {hasPerm(user,'vagas','create') && <button className={`btn ${tab==='nova'?'btn-primary':'btn-ghost'}`} onClick={()=>setTab('nova')}><Icon name="plus" size={13}/> Nova Vaga</button>}
        <button className={`btn ${tab==='minhas'?'btn-primary':'btn-ghost'}`} onClick={()=>setTab('minhas')}><Icon name="list" size={13}/> {hasPerm(user,'vagas','approve')?'Todas as vagas':'Minhas vagas'}</button>
      </div>

      {/* ── FORMULÁRIO (STEPPER) ── */}
      {tab==='nova' && hasPerm(user,'vagas','create') && (
        <div className="stepper-shell">
          <aside className="stepper-aside">
            <h3>Vaga de Emprego</h3>
            <h2>Inclua uma nova vaga</h2>
            <div className="step-list">
              {STEPS.map((s,i) => (
                <div key={i} className={`step-item ${i===step?'active':''} ${i<step?'done':''}`}>
                  <div className="step-num">{i<step?<Icon name="check" size={12}/>:i+1}</div>
                  <div className="step-label">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="footer-tip">Dados protegidos por autorização no servidor — só quem tem permissão de RH acessa o banco de vagas.</div>
          </aside>

          <div className="stepper-main">
            {ok && (
              <div style={{ textAlign:'center',padding:40 }}>
                <div style={{ fontSize:48,marginBottom:12 }}>✅</div>
                <div style={{ fontSize:18,fontWeight:600,color:'var(--ok)' }}>Vaga cadastrada com sucesso!</div>
                <p style={{ fontSize:13,color:'var(--ink-3)',marginTop:8 }}>Redirecionando para suas vagas...</p>
              </div>
            )}

            {!ok && step===0 && (
              <div>
                <h3>Cargo & Salário</h3>
                <p className="lead">Selecione o cargo e informe a remuneração.</p>
                <div className="field">
                  <label>Vaga Disponível <span className="req">*</span></label>
                  <select value={form.cargo} onChange={e=>set('cargo',e.target.value)}>
                    <option value="">— Selecione —</option>
                    {CARGOS_VAGA.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                  <div className="field">
                    <label>Salário</label>
                    <input type="text" value={form.salario} onChange={e=>set('salario',e.target.value)} placeholder="Ex: R$ 1.800,00"/>
                  </div>
                  <div className="field">
                    <label>Complemento (Comissões ou Outros)</label>
                    <input type="text" value={form.complemento} onChange={e=>set('complemento',e.target.value)} placeholder="Ex: + comissão"/>
                  </div>
                </div>
              </div>
            )}

            {!ok && step===1 && (
              <div>
                <h3>Dados de Contato</h3>
                <p className="lead">Informe o responsável e os canais de contato da loja.</p>
                <div className="field">
                  <label>Nome do Responsável <span className="req">*</span></label>
                  <input type="text" value={form.responsavel} onChange={e=>set('responsavel',e.target.value)} placeholder="Nome completo"/>
                </div>
                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                  <div className="field">
                    <label>Telefone para Contato</label>
                    <input type="text" value={form.telefone} onChange={e=>set('telefone', maskPhone(e.target.value))} placeholder="(XX) XXXXX-XXXX" maxLength={15}/>
                  </div>
                  <div className="field">
                    <label>E-mail</label>
                    <input type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="contato@loja.com"/>
                  </div>
                  <div className="field">
                    <label><Icon name="sparkle" size={12} style={{ verticalAlign:'middle',marginRight:4 }}/>Instagram</label>
                    <input type="text" value={form.instagram} onChange={e=>set('instagram',e.target.value)} placeholder="@sualojaosasco"/>
                  </div>
                  <div className="field">
                    <label><Icon name="megaphone" size={12} style={{ verticalAlign:'middle',marginRight:4 }}/>Facebook</label>
                    <input type="text" value={form.facebook} onChange={e=>set('facebook',e.target.value)} placeholder="facebook.com/sualojaosasco"/>
                  </div>
                </div>
              </div>
            )}

            {!ok && step===2 && (
              <div>
                <h3>Prazo & Detalhes</h3>
                <p className="lead">Defina a vigência e descreva a vaga.</p>
                <div className="field">
                  <label>Vaga Disponível Até <span className="req">*</span></label>
                  <input type="date" value={form.disponivelAte}
                    min={new Date().toISOString().slice(0,10)}
                    max={(() => { const d = new Date(); d.setDate(d.getDate()+90); return d.toISOString().slice(0,10); })()}
                    onChange={e=>set('disponivelAte',e.target.value)}/>
                  <div className="hint">Data mínima: hoje. Máximo 90 dias a partir de hoje.</div>
                </div>
                <div className="field">
                  <label>Detalhes da Vaga</label>
                  <textarea rows={5} value={form.detalhes} onChange={e=>set('detalhes',e.target.value)} placeholder="Descreva requisitos, horário, benefícios..."/>
                </div>
              </div>
            )}

            {!ok && step===3 && (
              <div>
                <h3>Revisão final</h3>
                <p className="lead">Confirme os dados antes de publicar a vaga.</p>
                <div style={{ background:'var(--gold-soft)',border:'1px solid var(--line-2)',borderRadius:10,padding:16,marginBottom:8 }}>
                  {[
                    ['Cargo',      form.cargo],
                    ['Salário',    form.salario || '—'],
                    ['Complemento',form.complemento || '—'],
                    ['Responsável',form.responsavel || '—'],
                    ['Telefone',   form.telefone || '—'],
                    ['E-mail',     form.email || '—'],
                    ['Instagram',  form.instagram || '—'],
                    ['Facebook',   form.facebook || '—'],
                    ['Disponível até', form.disponivelAte || '—'],
                  ].map(([k,v]) => (
                    <div key={k} className="review-row"><span className="k">{k}</span><span className="v">{v}</span></div>
                  ))}
                  {form.detalhes && <div style={{ marginTop:12,fontSize:12,color:'var(--ink-2)',borderTop:'1px solid var(--line)',paddingTop:10 }}><b>Detalhes:</b><br/>{form.detalhes}</div>}
                </div>
              </div>
            )}

            {erroForm && !ok && <div style={{ color:'var(--err)',fontSize:12,padding:'8px 12px',background:'var(--err-bg)',borderRadius:8,marginTop:12 }}>{erroForm}</div>}

            {!ok && (
              <div className="stepper-foot">
                <button className="btn btn-ghost" onClick={step===0?()=>{}:()=>setStep(s=>s-1)}>
                  <Icon name="arrow-left" size={13}/> {step===0?'Cancelar':'Voltar'}
                </button>
                {step < STEPS.length-1
                  ? <button className="btn btn-primary" onClick={()=>setStep(s=>s+1)} disabled={(step===0&&!form.cargo)||(step===1&&!form.responsavel)||(step===2&&!form.disponivelAte)}>
                      Continuar <Icon name="arrow-right" size={13}/>
                    </button>
                  : <button className="btn btn-gold" onClick={submit} disabled={busy}>
                      <Icon name="check" size={13}/> {busy?'Publicando...':'Cadastrar e publicar vaga'}
                    </button>
                }
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── LISTAGEM DE VAGAS ── */}
      {tab==='minhas' && (
        <div>
          {vagasLoading
            ? <div className="card" style={{ textAlign:'center',padding:32,color:'var(--ink-3)' }}>Carregando vagas...</div>
            : vagas.length === 0
              ? <div className="card" style={{ textAlign:'center',padding:40,color:'var(--err)',fontWeight:600 }}>Você não possui vagas cadastradas.</div>
              : (
                <div className="card" style={{ padding:0,overflow:'hidden' }}>
                  <div style={{ overflowX:'auto' }}>
                    <table className="users-table">
                      <thead>
                        <tr>
                          <th>ID</th><th>Cargo</th><th>Salário</th><th>Complemento</th>
                          <th>Publicado em</th><th>Disponível até</th><th>Status</th><th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vagas.map(v=>(
                          <tr key={v.id}>
                            <td><code style={{ fontSize:11,background:'var(--surface-2)',padding:'2px 5px',borderRadius:4 }}>{v.id}</code></td>
                            <td style={{ fontWeight:600,fontSize:13 }}>{v.cargo}</td>
                            <td style={{ fontSize:12 }}>{v.salario||'—'}</td>
                            <td style={{ fontSize:12 }}>{v.complemento||'—'}</td>
                            <td style={{ fontSize:11,color:'var(--ink-3)' }}>{new Date(v.criadoEm).toLocaleDateString('pt-BR')}</td>
                            <td style={{ fontSize:11,color:'var(--ink-3)' }}>{v.disponivelAte||'—'}</td>
                            <td>
                              <span style={{ fontSize:11,fontWeight:600,padding:'3px 8px',borderRadius:999,background:v.ativo?'var(--ok-bg)':'var(--surface-2)',color:v.ativo?'var(--ok)':'var(--ink-3)' }}>
                                {v.ativo?'✅ Ativa':'⛔ Inativa'}
                              </span>
                            </td>
                            <td>
                              <div style={{ display:'flex',gap:6 }}>
                                {hasPerm(user,'vagas','edit') && (
                                  <button className="btn btn-ghost btn-sm" onClick={()=>desativar(v.id)} title={v.ativo?'Desativar':'Ativar'}>
                                    {v.ativo?<Icon name="x" size={13}/>:<Icon name="check" size={13}/>}
                                  </button>
                                )}
                                {hasPerm(user,'vagas','delete') && (
                                  <button className="btn btn-danger btn-sm" onClick={()=>excluir(v.id)} title="Excluir"><Icon name="trash" size={13}/></button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
          }
        </div>
      )}
    </div>
  );
}


export function CandidatoDetalheModal({ candidato, user, onClose, onAlterado }) {
  const [fotoUrl, setFotoUrl] = useState(null);
  const [fotoErro, setFotoErro] = useState(false);
  const [baixandoCv, setBaixandoCv] = useState(false);
  const [alterandoStatus, setAlterandoStatus] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [erro, setErro] = useState('');

  const c = candidato;

  /* Foto vem como blob autenticado do R2, não mais base64 embutido no
     objeto — mesmo padrão de SegFoto. Sem isso, cada currículo carregaria a
     imagem inteira mesmo quando ninguém abre o detalhe. */
  useEffect(() => {
    if (!c) return;
    let vivo = true, criado = null;
    setFotoUrl(null); setFotoErro(false);
    if (c.temFoto) {
      API.curriculos.fotoUrl(c.id)
        .then(u => { if (vivo) { criado = u; setFotoUrl(u); } else URL.revokeObjectURL(u); })
        .catch(() => { if (vivo) setFotoErro(true); });
    }
    return () => { vivo = false; if (criado) URL.revokeObjectURL(criado); };
  }, [c && c.id]);

  if (!c) return null;

  const calcIdade = (nasc) => {
    if (!nasc) return null;
    const hoje = new Date();
    const [ano,mes,dia] = nasc.split('-').map(Number);
    let idade = hoje.getFullYear() - ano;
    if (hoje.getMonth()+1 < mes || (hoje.getMonth()+1 === mes && hoje.getDate() < dia)) idade--;
    return idade;
  };
  const idade = c.dataNascimento ? calcIdade(c.dataNascimento) : null;
  const st = STATUS_CURRICULO[c.status] || STATUS_CURRICULO.recebido;

  const baixarCv = async () => {
    setBaixandoCv(true); setErro('');
    try { await API.curriculos.baixarCv(c.id, `curriculo-${c.nome||'candidato'}.pdf`); }
    catch (e) { setErro(e.message || 'Não foi possível baixar o CV.'); }
    setBaixandoCv(false);
  };

  const mudarStatus = async (status) => {
    setAlterandoStatus(true); setErro('');
    try { await API.curriculos.mudarStatus(c.id, status); onAlterado && onAlterado(); }
    catch (e) { setErro(e.message || 'Não foi possível alterar o status.'); }
    setAlterandoStatus(false);
  };

  const excluir = async () => {
    if (!confirm('Excluir este currículo? Os arquivos anexados também serão apagados.')) return;
    setExcluindo(true); setErro('');
    try { await API.curriculos.remover(c.id); onAlterado && onAlterado(); onClose(); }
    catch (e) { setErro(e.message || 'Não foi possível excluir o currículo.'); setExcluindo(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" style={{ maxWidth:640, width:'95%', maxHeight:'90vh', overflowY:'auto' }} onClick={e=>e.stopPropagation()}>
        <button className="close-x" onClick={onClose}><Icon name="x" size={16}/></button>

        {/* Cabeçalho do candidato */}
        <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:20, paddingBottom:16, borderBottom:'1px solid var(--line)' }}>
          {fotoUrl
            ? <img src={fotoUrl} alt="foto" style={{ width:72, height:72, borderRadius:'50%', objectFit:'cover', border:'3px solid var(--gold)', flexShrink:0, cursor:'pointer' }}
                onClick={()=>window.open(fotoUrl,'_blank')}/>
            : <div style={{ width:72, height:72, borderRadius:'50%', background:'var(--navy)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--gold)', fontSize:26, fontWeight:700, flexShrink:0 }}>
                {(c.nome||'?').charAt(0).toUpperCase()}
              </div>
          }
          <div style={{ flex:1 }}>
            <div style={{ fontSize:20, fontWeight:700, color:'var(--ink)', letterSpacing:'-0.02em' }}>{c.nome||'—'}</div>
            <div style={{ fontSize:13, color:'var(--ink-3)', marginTop:2 }}>{[c.cargo1,c.cargo2,c.cargo3,c.cargo4].filter(Boolean).join(' · ')||'—'}</div>
            <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap' }}>
              <span className={`status-pill ${st.classe}`}><span className="ind"/>{st.label}</span>
              {c.sexo && <span className="status-pill info"><span className="ind"/>{c.sexo==='Masculino'?'M':'F'}</span>}
              {idade && <span className="status-pill muted"><span className="ind"/>{idade} anos</span>}
              {c.temFoto && !fotoErro && <span className="status-pill ok"><span className="ind"/>🖼 Com Foto</span>}
              {c.temCurriculo && <span className="status-pill ok"><span className="ind"/>📄 Com CV</span>}
            </div>
          </div>
        </div>

        {/* Grade de informações */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 24px', marginBottom:16 }}>
          {[
            ['CPF', c.cpf ? c.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '—'],
            ['Data de Nascimento', c.dataNascimento ? new Date(c.dataNascimento+'T12:00:00').toLocaleDateString('pt-BR') : '—'],
            ['Sexo', c.sexo||'—'],
            ['Estado Civil', c.estadoCivil||'—'],
            ['Escolaridade', c.escolaridade||'—'],
            ['Nacionalidade', c.nacionalidade||'—'],
          ].map(([k,v])=>(
            <div key={k} style={{ fontSize:12 }}>
              <div style={{ color:'var(--ink-4)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', fontSize:10, marginBottom:2 }}>{k}</div>
              <div style={{ color:'var(--ink)', fontWeight:500, fontFamily: k==='CPF' ? "'JetBrains Mono',monospace" : undefined }}>{v}</div>
            </div>
          ))}
        </div>

        <div className="divider"/>

        {/* Contato */}
        <div style={{ marginBottom:14 }}>
          <div className="label-eyebrow">Contato</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 24px', fontSize:12 }}>
            {c.email          && <div>✉ <span style={{ color:'var(--ink-2)' }}>{c.email}</span></div>}
            {c.celular        && <div>📱 <span style={{ color:'var(--ink-2)' }}>{c.celular}</span></div>}
            {c.telResidencial && <div>📞 <span style={{ color:'var(--ink-2)' }}>{c.telResidencial}</span></div>}
            {c.telOutro       && <div>☎ <span style={{ color:'var(--ink-2)' }}>{c.telOutro}</span></div>}
          </div>
        </div>

        {/* Endereço */}
        {(c.endereco||c.cidade||c.estado) && (
          <>
            <div className="divider"/>
            <div style={{ marginBottom:14 }}>
              <div className="label-eyebrow">Endereço</div>
              <div style={{ fontSize:12, color:'var(--ink-2)' }}>
                {[c.endereco, c.numero, c.complemento, c.bairro].filter(Boolean).join(', ')}
                {c.cidade && <span> — {c.cidade}{c.estado?' / '+c.estado:''}</span>}
                {c.cep && <span> · CEP {c.cep}</span>}
              </div>
            </div>
          </>
        )}

        {/* Redes sociais */}
        {(c.linkedin||c.instagram||c.facebook) && (
          <>
            <div className="divider"/>
            <div style={{ marginBottom:14 }}>
              <div className="label-eyebrow">Redes Sociais</div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', fontSize:12 }}>
                {c.linkedin  && <a href={c.linkedin.startsWith('http')?c.linkedin:'https://'+c.linkedin} target="_blank" rel="noopener noreferrer" style={{ color:'var(--info)', textDecoration:'none', display:'flex', alignItems:'center', gap:4 }}>🔗 LinkedIn</a>}
                {c.instagram && <span style={{ color:'var(--ink-2)' }}>📷 {c.instagram}</span>}
                {c.facebook  && <span style={{ color:'var(--ink-2)' }}>📘 {c.facebook}</span>}
              </div>
            </div>
          </>
        )}

        {/* Histórico */}
        {c.historico && (
          <>
            <div className="divider"/>
            <div style={{ marginBottom:14 }}>
              <div className="label-eyebrow">Histórico</div>
              <div style={{ fontSize:12, color:'var(--ink-2)', lineHeight:1.55 }}>{c.historico}</div>
            </div>
          </>
        )}

        {/* Anexos — Foto e CV */}
        {(c.temFoto || c.temCurriculo) && (
          <>
            <div className="divider"/>
            <div style={{ marginBottom:4 }}>
              <div className="label-eyebrow">Anexos</div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginTop:8 }}>
                {c.temFoto && fotoUrl && (
                  <a href={fotoUrl} target="_blank" rel="noopener noreferrer"
                    style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'7px 14px', background:'var(--info-bg)', color:'var(--info)', borderRadius:8, fontSize:12, fontWeight:600, textDecoration:'none', border:'1px solid var(--info)' }}>
                    🖼 Abrir Foto
                  </a>
                )}
                {c.temCurriculo && (
                  <button className="btn btn-sm" disabled={baixandoCv} onClick={baixarCv}
                    style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'7px 14px', background:'var(--ok-bg)', color:'var(--ok)', borderRadius:8, fontSize:12, fontWeight:600, border:'1px solid var(--ok)' }}>
                    📄 {baixandoCv?'Baixando...':'Baixar CV'}
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {/* Status da candidatura — quem tem `curriculos.edit` decide */}
        {hasPerm(user,'curriculos','edit') && (
          <>
            <div className="divider"/>
            <div style={{ marginBottom:4 }}>
              <div className="label-eyebrow">Status da candidatura</div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
                {Object.entries(STATUS_CURRICULO).map(([valor,{label}]) => (
                  <button key={valor} className={`btn btn-sm ${c.status===valor?'btn-primary':'btn-ghost'}`}
                    disabled={alterandoStatus || c.status===valor} onClick={()=>mudarStatus(valor)}>
                    {label}
                  </button>
                ))}
              </div>
              {c.decididoEm && <div className="hint" style={{ marginTop:6 }}>Decidido em {new Date(c.decididoEm).toLocaleDateString('pt-BR')}{c.decididoPorNome?' por '+c.decididoPorNome:''}.</div>}
            </div>
          </>
        )}

        {erro && <div style={{ color:'var(--err)',fontSize:12,padding:'8px 12px',background:'var(--err-bg)',borderRadius:8,marginTop:14 }}>{erro}</div>}

        <div style={{ marginTop:20, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          {hasPerm(user,'curriculos','delete')
            ? <button className="btn btn-danger btn-sm" disabled={excluindo} onClick={excluir}>
                <Icon name="trash" size={13}/> {excluindo?'Excluindo...':'Excluir'}
              </button>
            : <span/>
          }
          <button className="btn btn-ghost" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}


export function ConsultarCurriculosScreen({ user }) {
  if (!hasPerm(user,'curriculos','view')) return <Blocked/>;

  const [filtros, setFiltros] = useState({
    sexo:'', nacionalidade:'', status:'', faixaEtariaDe:'', faixaEtariaAte:'',
    comFoto:'', comCv:'', comLinkedin:'', comFacebook:'', comInstagram:'',
    escolaridades:[], estadoCivil:[], cargos:[],
  });
  const [resultados, setResultados] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [erroBusca, setErroBusca] = useState('');
  const [candidatoSel, setCandidatoSel] = useState(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

  const setF = (k,v) => setFiltros(f=>({...f,[k]:v}));

  /* A filtragem passou a acontecer no servidor (SQL), não mais varrendo o
     array inteiro no navegador — ver montarFiltroCurriculos() no worker. */
  const buscar = async () => {
    setBuscando(true); setErroBusca('');
    try {
      const r = await API.curriculos.listar({
        sexo: filtros.sexo, nacionalidade: filtros.nacionalidade, status: filtros.status,
        comFoto: filtros.comFoto, comCv: filtros.comCv, comLinkedin: filtros.comLinkedin,
        comFacebook: filtros.comFacebook, comInstagram: filtros.comInstagram,
        escolaridade: filtros.escolaridades.join(','), estadoCivil: filtros.estadoCivil.join(','),
        cargo: filtros.cargos.join(','), idadeDe: filtros.faixaEtariaDe, idadeAte: filtros.faixaEtariaAte,
      });
      setResultados(r.curriculos || []);
    } catch (e) {
      setErroBusca(e.message || 'Não foi possível buscar os currículos.');
      setResultados([]);
    }
    setBuscando(false);
  };

  const limpar = () => {
    setFiltros({ sexo:'', nacionalidade:'', status:'', faixaEtariaDe:'', faixaEtariaAte:'',
      comFoto:'', comCv:'', comLinkedin:'', comFacebook:'', comInstagram:'', escolaridades:[], estadoCivil:[], cargos:[] });
    setResultados(null);
  };

  const abrirDetalhe = async (id) => {
    setCarregandoDetalhe(true);
    try { const r = await API.curriculos.detalhe(id); setCandidatoSel(r.curriculo); }
    catch (e) { alert(e.message || 'Não foi possível abrir o currículo.'); }
    setCarregandoDetalhe(false);
  };

  const faixasEtarias = Array.from({length:53},(_,i)=>i+18);

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title">
          <h2>Banco de Currículos</h2>
          <p>Consulte candidatos cadastrados no portal de empregos do shopping</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-head" style={{ marginBottom:16 }}>
          <h3><Icon name="filter" size={14} style={{ marginRight:6,verticalAlign:'middle' }}/>Filtro / Busca de Currículos</h3>
        </div>

        {/* Linha 1 — Faixa Etária */}
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
          <div className="field" style={{ margin:0 }}>
            <label>Faixa Etária — De</label>
            <select value={filtros.faixaEtariaDe} onChange={e=>setF('faixaEtariaDe',e.target.value)}>
              <option value="">—</option>
              {faixasEtarias.map(n=><option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="field" style={{ margin:0 }}>
            <label>Até</label>
            <select value={filtros.faixaEtariaAte} onChange={e=>setF('faixaEtariaAte',e.target.value)}>
              <option value="">—</option>
              {faixasEtarias.map(n=><option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {/* Linha 2 — Sexo, Nacionalidade, Com Foto/CV/LinkedIn/Facebook/Instagram */}
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:12,marginBottom:16 }}>
          <div>
            <div style={{ fontSize:11,fontWeight:600,color:'var(--ink-3)',marginBottom:6 }}>Sexo</div>
            {['Masculino','Feminino'].map(s=>(
              <label key={s} style={{ display:'inline-flex',alignItems:'center',gap:5,fontSize:12,marginRight:12,cursor:'pointer' }}>
                <input type="radio" name="sexo" value={s} checked={filtros.sexo===s} onChange={()=>setF('sexo',s)}/> {s}
              </label>
            ))}
          </div>
          <div>
            <div style={{ fontSize:11,fontWeight:600,color:'var(--ink-3)',marginBottom:6 }}>Nacionalidade</div>
            {['Brasileira','Estrangeira'].map(s=>(
              <label key={s} style={{ display:'inline-flex',alignItems:'center',gap:5,fontSize:12,marginRight:12,cursor:'pointer' }}>
                <input type="radio" name="nac" value={s} checked={filtros.nacionalidade===s} onChange={()=>setF('nacionalidade',s)}/> {s}
              </label>
            ))}
          </div>
          <div>
            <div style={{ fontSize:11,fontWeight:600,color:'var(--ink-3)',marginBottom:6 }}>Com Foto? / Com CV?</div>
            {[['comFoto','Com Foto?'],['comCv','Com CV?']].map(([k,lbl])=>(
              <div key={k} style={{ marginBottom:4 }}>
                <span style={{ fontSize:11,color:'var(--ink-3)',marginRight:6 }}>{lbl}</span>
                {['SIM','NÃO'].map(v=>(
                  <label key={v} style={{ display:'inline-flex',alignItems:'center',gap:4,fontSize:12,marginRight:8,cursor:'pointer' }}>
                    <input type="radio" name={k} value={v} checked={filtros[k]===v} onChange={()=>setF(k,v)}/> {v}
                  </label>
                ))}
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontSize:11,fontWeight:600,color:'var(--ink-3)',marginBottom:6 }}>LinkedIn? / Facebook? / Instagram?</div>
            {[['comLinkedin','Com LinkedIn?'],['comFacebook','Com Facebook?'],['comInstagram','Com Instagram?']].map(([k,lbl])=>(
              <div key={k} style={{ marginBottom:4 }}>
                <span style={{ fontSize:11,color:'var(--ink-3)',marginRight:6 }}>{lbl}</span>
                {['SIM','NÃO'].map(v=>(
                  <label key={v} style={{ display:'inline-flex',alignItems:'center',gap:4,fontSize:12,marginRight:8,cursor:'pointer' }}>
                    <input type="radio" name={k} value={v} checked={filtros[k]===v} onChange={()=>setF(k,v)}/> {v}
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Status da candidatura */}
        <div className="field" style={{ maxWidth:260, marginBottom:16 }}>
          <label>Status da candidatura</label>
          <select value={filtros.status} onChange={e=>setF('status',e.target.value)}>
            <option value="">— Todos —</option>
            {Object.entries(STATUS_CURRICULO).map(([v,{label}])=><option key={v} value={v}>{label}</option>)}
          </select>
        </div>

        {/* Escolaridade */}
        <CheckboxGroup label="Escolaridade" items={ESCOLARIDADES} selected={filtros.escolaridades} onChange={v=>setF('escolaridades',v)}/>

        {/* Estado Civil */}
        <CheckboxGroup label="Estado Civil" items={ESTADO_CIVIL} selected={filtros.estadoCivil} onChange={v=>setF('estadoCivil',v)}/>

        {/* Cargo Pretendido */}
        <CheckboxGroup label="Cargo Pretendido" items={CARGOS_CURRICULO} selected={filtros.cargos} onChange={v=>setF('cargos',v)}/>

        <div style={{ display:'flex',gap:10,justifyContent:'center',marginTop:8 }}>
          <button className="btn btn-ghost" onClick={limpar}><Icon name="x" size={13}/> Limpar filtros</button>
          <button className="btn btn-gold btn-lg" onClick={buscar} disabled={buscando}>
            <Icon name="search" size={14}/> {buscando?'Buscando...':'Clique aqui para filtrar os currículos cadastrados'}
          </button>
        </div>
        {erroBusca && <div style={{ color:'var(--err)',fontSize:12,padding:'8px 12px',background:'var(--err-bg)',borderRadius:8,marginTop:12,textAlign:'center' }}>{erroBusca}</div>}
      </div>

      {/* Resultados — tabela estilo planilha */}
      {resultados !== null && (
        <div>
          <div style={{ fontSize:13,fontWeight:600,color:'var(--ink-2)',marginBottom:12 }}>
            {resultados.length === 0
              ? '❌ Nenhum currículo encontrado com os filtros selecionados.'
              : `✅ ${resultados.length} currículo${resultados.length!==1?'s':''} encontrado${resultados.length!==1?'s':''}.`}
          </div>

          {resultados.length > 0 && (
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'var(--navy)', color:'white' }}>
                      <th style={{ padding:'11px 16px', textAlign:'left', fontWeight:600, fontSize:11, letterSpacing:'0.06em', textTransform:'uppercase', whiteSpace:'nowrap' }}>Nome</th>
                      <th style={{ padding:'11px 12px', textAlign:'left', fontWeight:600, fontSize:11, letterSpacing:'0.06em', textTransform:'uppercase', whiteSpace:'nowrap' }}>Cargo</th>
                      <th style={{ padding:'11px 12px', textAlign:'center', fontWeight:600, fontSize:11, letterSpacing:'0.06em', textTransform:'uppercase', whiteSpace:'nowrap' }}>Idade</th>
                      <th style={{ padding:'11px 12px', textAlign:'center', fontWeight:600, fontSize:11, letterSpacing:'0.06em', textTransform:'uppercase', whiteSpace:'nowrap' }}>Sexo</th>
                      <th style={{ padding:'11px 12px', textAlign:'center', fontWeight:600, fontSize:11, letterSpacing:'0.06em', textTransform:'uppercase', whiteSpace:'nowrap' }}>CV</th>
                      <th style={{ padding:'11px 12px', textAlign:'center', fontWeight:600, fontSize:11, letterSpacing:'0.06em', textTransform:'uppercase', whiteSpace:'nowrap' }}>Foto</th>
                      <th style={{ padding:'11px 12px', textAlign:'center', fontWeight:600, fontSize:11, letterSpacing:'0.06em', textTransform:'uppercase', whiteSpace:'nowrap' }}>Status</th>
                      <th style={{ padding:'11px 16px', textAlign:'center', fontWeight:600, fontSize:11, letterSpacing:'0.06em', textTransform:'uppercase', whiteSpace:'nowrap' }}>Cadastro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultados.map((c,i) => {
                      const hoje = new Date();
                      const nasc = c.dataNascimento;
                      let idade = c.idade || '—';
                      if (nasc) {
                        const [ano,mes,dia] = nasc.split('-').map(Number);
                        let id = hoje.getFullYear() - ano;
                        if (hoje.getMonth()+1 < mes || (hoje.getMonth()+1===mes && hoje.getDate()<dia)) id--;
                        idade = id;
                      }
                      const sexo = c.sexo === 'Masculino' ? 'M' : c.sexo === 'Feminino' ? 'F' : (c.sexo||'—');
                      const dataCad = c.criadoEm ? new Date(c.criadoEm).toLocaleDateString('pt-BR') : '—';
                      const temCv   = !!c.temCurriculo;
                      const temFoto = !!c.temFoto;
                      return (
                        <tr key={i}
                          onClick={() => abrirDetalhe(c.id)}
                          style={{ background: i%2===0 ? 'var(--surface)' : 'var(--surface-2)', cursor:'pointer', transition:'background 0.12s' }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--gold-soft)'}
                          onMouseLeave={e => e.currentTarget.style.background = i%2===0 ? 'var(--surface)' : 'var(--surface-2)'}
                        >
                          <td style={{ padding:'10px 16px', fontWeight:600, color:'var(--navy)', borderBottom:'1px solid var(--line)', whiteSpace:'nowrap' }}>{c.nome||'—'}</td>
                          <td style={{ padding:'10px 12px', borderBottom:'1px solid var(--line)', color:'var(--ink-2)', fontSize:12 }}>{c.cargo1||'—'}</td>
                          <td style={{ padding:'10px 12px', textAlign:'center', borderBottom:'1px solid var(--line)', color:'var(--ink-2)' }}>{idade}</td>
                          <td style={{ padding:'10px 12px', textAlign:'center', borderBottom:'1px solid var(--line)' }}>
                            <span style={{ fontWeight:700, color: sexo==='F' ? 'var(--err)' : 'var(--info)' }}>{sexo}</span>
                          </td>
                          <td style={{ padding:'10px 12px', textAlign:'center', borderBottom:'1px solid var(--line)' }}>
                            <span style={{ fontWeight:700, color: temCv ? 'var(--ok)' : 'var(--ink-4)' }}>{temCv ? 'S' : 'N'}</span>
                          </td>
                          <td style={{ padding:'10px 12px', textAlign:'center', borderBottom:'1px solid var(--line)' }}>
                            <span style={{ fontWeight:700, color: temFoto ? 'var(--ok)' : 'var(--ink-4)' }}>{temFoto ? 'S' : 'N'}</span>
                          </td>
                          <td style={{ padding:'10px 12px', textAlign:'center', borderBottom:'1px solid var(--line)' }}>
                            <span className={`status-pill ${(STATUS_CURRICULO[c.status]||STATUS_CURRICULO.recebido).classe}`} style={{ fontSize:11 }}>
                              <span className="ind"/>{(STATUS_CURRICULO[c.status]||STATUS_CURRICULO.recebido).label}
                            </span>
                          </td>
                          <td style={{ padding:'10px 16px', textAlign:'center', borderBottom:'1px solid var(--line)', color:'var(--ink-3)', fontSize:12 }}>{dataCad}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ padding:'10px 16px', borderTop:'1px solid var(--line)', fontSize:11, color:'var(--ink-4)', background:'var(--surface-2)' }}>
                {carregandoDetalhe ? 'Carregando…' : 'Clique em um candidato para ver os dados completos'}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal detalhe do candidato */}
      {candidatoSel && (
        <CandidatoDetalheModal candidato={candidatoSel} user={user}
          onClose={()=>setCandidatoSel(null)}
          onAlterado={async () => {
            const r = await API.curriculos.detalhe(candidatoSel.id);
            setCandidatoSel(r.curriculo);
            buscar();
          }}/>
      )}
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════
   PAINEL DE VAGAS — CONSULTAR VAGAS (público para candidatos)
   ════════════════════════════════════════════════════════════════ */
export function ConsultarVagasScreen({ user, onNav }) {
  if (!hasPerm(user,'painel','view')) return <Blocked/>;
  const [vagas, setVagas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // vaga selecionada para candidatura rápida

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const r = await API.vagas.abertas(); setVagas(r.vagas || []); }
      catch (e) { setVagas([]); }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title">
          <h2>🏢 Vagas Disponíveis</h2>
          <p>Oportunidades abertas nas lojas do Shopping Osasco Plaza</p>
        </div>
        {hasPerm(user,'painel','create') && (
          <button className="btn btn-gold" onClick={()=>onNav('candidatar')}>
            <Icon name="send" size={13}/> Enviar meu currículo
          </button>
        )}
      </div>

      {loading && <div className="card" style={{ textAlign:'center',padding:32,color:'var(--ink-3)' }}>Carregando vagas...</div>}

      {!loading && vagas.length === 0 && (
        <div className="card" style={{ textAlign:'center',padding:48 }}>
          <Icon name="briefcase" size={36} style={{ color:'var(--ink-4)',display:'block',margin:'0 auto 12px' }}/>
          <div style={{ fontWeight:600,color:'var(--ink-2)',marginBottom:6 }}>Nenhuma vaga disponível no momento.</div>
          <div style={{ fontSize:13,color:'var(--ink-3)' }}>Novas vagas são publicadas regularmente. Volte em breve!</div>
        </div>
      )}

      {!loading && vagas.map(v => (
        <div key={v.id} className="card" style={{ marginBottom:14,borderLeft:'4px solid var(--gold)' }}>
          <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16 }}>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:6 }}>
                <div style={{ width:38,height:38,borderRadius:10,background:'var(--navy)',color:'var(--gold)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                  <Icon name="briefcase" size={18}/>
                </div>
                <div>
                  <div style={{ fontSize:17,fontWeight:700,color:'var(--ink)',letterSpacing:'-0.01em' }}>{v.cargo}</div>
                  <div style={{ fontSize:11,color:'var(--ink-3)',marginTop:1 }}>
                    Publicada em {new Date(v.criadoEm).toLocaleDateString('pt-BR')} · Disponível até {v.disponivelAte}
                  </div>
                </div>
              </div>

              <div style={{ display:'flex',gap:12,flexWrap:'wrap',margin:'10px 0' }}>
                {v.salario && <span style={{ fontSize:12,background:'var(--ok-bg)',color:'var(--ok)',padding:'3px 10px',borderRadius:999,fontWeight:600 }}>💰 {v.salario}{v.complemento?' + '+v.complemento:''}</span>}
              </div>

              {v.detalhes && (
                <div style={{ fontSize:13,color:'var(--ink-2)',lineHeight:1.55,marginTop:6,borderTop:'1px solid var(--line)',paddingTop:10 }}>
                  {v.detalhes}
                </div>
              )}

              <div style={{ display:'flex',gap:16,marginTop:10,fontSize:12,color:'var(--ink-3)',flexWrap:'wrap' }}>
                {v.responsavel && <span>👤 {v.responsavel}</span>}
                {v.telefone && <span>📞 {v.telefone}</span>}
                {v.email && <span>✉ {v.email}</span>}
                {v.instagram && <span>📷 {v.instagram}</span>}
                {v.facebook && <span>📘 {v.facebook}</span>}
              </div>
            </div>

            <div style={{ display:'flex',flexDirection:'column',gap:8,flexShrink:0 }}>
              {hasPerm(user,'painel','create') && (
                <button className="btn btn-primary" onClick={()=>setModal(v)}>
                  <Icon name="send" size={13}/> Candidatar-me
                </button>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Modal candidatura rápida */}
      {modal && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth:460,textAlign:'center' }}>
            <button className="close-x" onClick={()=>setModal(null)}><Icon name="x" size={16}/></button>
            <div style={{ fontSize:36,marginBottom:8 }}>🚀</div>
            <h3>Candidatura — {modal.cargo}</h3>
            <p style={{ fontSize:13,color:'var(--ink-3)',margin:'10px 0 20px',lineHeight:1.5 }}>
              Para se candidatar a esta vaga, acesse o formulário completo de currículo. Seus dados ficam disponíveis para a equipe de RH avaliar a candidatura.
            </p>
            <div style={{ background:'var(--gold-soft)',borderRadius:8,padding:'12px 14px',fontSize:12,color:'#7c6326',marginBottom:20,textAlign:'left' }}>
              <b>Vaga:</b> {modal.cargo}<br/>
              {modal.salario && <><b>Salário:</b> {modal.salario}{modal.complemento?' + '+modal.complemento:''}<br/></>}
              {modal.responsavel && <><b>Responsável:</b> {modal.responsavel}</>}
            </div>
            <div style={{ display:'flex',gap:10 }}>
              <button className="btn btn-ghost" style={{ flex:1 }} onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-gold" style={{ flex:1 }} onClick={()=>{ setModal(null); onNav('candidatar'); }}>
                <Icon name="send" size={13}/> Preencher currículo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Step 1: Endereço com CEP automático ── */
export function StepEndereco({ form, set }) {
  const [buscandoCep, setBuscandoCep] = React.useState(false);
  const [cepErro, setCepErro] = React.useState('');

  const handleCep = async (raw) => {
    const masked = maskCep(raw);
    set('cep', masked);
    const digits = masked.replace(/\D/g,'');
    if (digits.length === 8) {
      setBuscandoCep(true); setCepErro('');
      try {
        const r = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
        const d = await r.json();
        if (d.erro) { setCepErro('CEP não encontrado.'); }
        else {
          set('endereco', d.logradouro || '');
          set('bairro',   d.bairro     || '');
          set('cidade',   d.localidade || '');
          set('estado',   d.uf         || '');
          setCepErro('');
        }
      } catch { setCepErro('Erro ao buscar CEP. Verifique sua conexão.'); }
      setBuscandoCep(false);
    }
  };

  return (
    <div>
      <h3>Endereço residencial</h3>
      <p className="lead">Informe seu endereço atual.</p>

      {/* CEP primeiro para preencher automaticamente */}
      <div style={{ display:'grid', gridTemplateColumns:'180px 1fr', gap:12, marginBottom:0 }}>
        <div className="field">
          <label>CEP</label>
          <div style={{ position:'relative' }}>
            <input type="text" value={form.cep} onChange={e=>handleCep(e.target.value)} placeholder="00000-000" maxLength={9}
              style={{ paddingRight: buscandoCep ? 36 : 12 }}/>
            {buscandoCep && (
              <div style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', width:16, height:16,
                border:'2px solid var(--navy)', borderTopColor:'transparent', borderRadius:'50%',
                animation:'spin 0.7s linear infinite' }}/>
            )}
          </div>
          {cepErro && <div className="hint" style={{ color:'var(--err)' }}>{cepErro}</div>}
          {buscandoCep && <div className="hint">Buscando endereço...</div>}
        </div>
        <div className="field">
          <label>Endereço <span className="req">*</span></label>
          <input type="text" value={form.endereco} onChange={e=>set('endereco',e.target.value)} placeholder="Rua, Avenida..."/>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:12 }}>
        <div className="field"><label>Número</label><input type="text" value={form.numero} onChange={e=>set('numero',e.target.value)}/></div>
        <div className="field"><label>Complemento</label><input type="text" value={form.complemento} onChange={e=>set('complemento',e.target.value)} placeholder="Apto, Bloco..."/></div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1fr', gap:12 }}>
        <div className="field"><label>Bairro</label><input type="text" value={form.bairro} onChange={e=>set('bairro',e.target.value)}/></div>
        <div className="field"><label>Cidade <span className="req">*</span></label><input type="text" value={form.cidade} onChange={e=>set('cidade',e.target.value)}/></div>
        <div className="field"><label>Estado</label>
          <select value={form.estado} onChange={e=>set('estado',e.target.value)}>
            <option value="">—</option>
            {ESTADOS_BR.map(e=><option key={e}>{e}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}


/* ── Step 2: Contato com máscaras de telefone ── */
export function StepContato({ form, set }) {
  return (
    <div>
      <h3>Dados para contato</h3>
      <p className="lead">Informe como podemos te encontrar.</p>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div className="field"><label>E-mail</label><input type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="seu@email.com"/></div>
        <div className="field">
          <label>Celular</label>
          <input type="text" value={form.celular}
            onChange={e=>set('celular', maskPhone(e.target.value))}
            placeholder="(XX) XXXXX-XXXX" maxLength={15}/>
        </div>
        <div className="field">
          <label>Tel. residencial</label>
          <input type="text" value={form.telResidencial}
            onChange={e=>set('telResidencial', maskPhone(e.target.value))}
            placeholder="(XX) XXXXX-XXXX" maxLength={15}/>
        </div>
        <div className="field">
          <label>Tel. outro</label>
          <input type="text" value={form.telOutro}
            onChange={e=>set('telOutro', maskPhone(e.target.value))}
            placeholder="(XX) XXXXX-XXXX" maxLength={15}/>
        </div>
        <div className="field"><label>Instagram</label><input type="text" value={form.instagram} onChange={e=>set('instagram',e.target.value)} placeholder="@seuinstagram"/></div>
        <div className="field"><label>Facebook</label><input type="text" value={form.facebook} onChange={e=>set('facebook',e.target.value)} placeholder="facebook.com/seuperfil"/></div>
        <div className="field" style={{ gridColumn:'1/-1' }}><label>LinkedIn</label><input type="text" value={form.linkedin} onChange={e=>set('linkedin',e.target.value)} placeholder="linkedin.com/in/seuperfil"/></div>
      </div>
    </div>
  );
}


export function CandidatarVagaScreen({ user, onChange }) {
  if (!hasPerm(user,'painel','create')) return <Blocked/>;

  const STEPS = [
    { label:'Dados Pessoais' },
    { label:'Endereço' },
    { label:'Contato & Redes' },
    { label:'Histórico & Cargos' },
    { label:'Revisão' },
  ];

  const empty = {
    nome:'', cpf:'', estadoCivil:'', nacionalidade:'Brasileira', dataNascimento:'', sexo:'',
    escolaridade:'',
    endereco:'', numero:'', complemento:'', bairro:'', cidade:'', estado:'', cep:'',
    email:'', telResidencial:'', celular:'', telOutro:'', facebook:'', linkedin:'', instagram:'',
    historico:'', cargo1:'', cargo2:'', cargo3:'', cargo4:'',
  };

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  /* Foto/CV ficam como `File` em memória, não mais como data URL no form —
     viram bytes crus enviados ao worker DEPOIS do currículo já estar
     gravado, mesmo padrão de anexarFoto em Segurança Patrimonial. Nunca
     mais base64 dentro do JSON sincronizado. */
  const [foto, setFoto] = useState(null);
  const [curriculoArq, setCurriculoArq] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState('');
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const validateStep = () => {
    if (step===0) {
      if (!form.nome.trim()) { setErr('Nome completo é obrigatório.'); return false; }
      if (!form.cpf.trim())  { setErr('CPF é obrigatório.'); return false; }
      if (!validarCPF(form.cpf.replace(/\D/g,''))) { setErr('CPF inválido. Verifique os dígitos.'); return false; }
      if (!form.sexo)        { setErr('Selecione o sexo.'); return false; }
      if (!form.escolaridade){ setErr('Selecione a escolaridade.'); return false; }
    }
    if (step===1) {
      if (!form.endereco.trim()) { setErr('Endereço é obrigatório.'); return false; }
      if (!form.cidade.trim())   { setErr('Cidade é obrigatória.'); return false; }
    }
    if (step===2) {
      if (!form.email.trim() && !form.celular.trim()) { setErr('Informe ao menos e-mail ou celular.'); return false; }
    }
    if (step===3) {
      if (!form.cargo1.trim()) { setErr('Informe ao menos um cargo pretendido.'); return false; }
    }
    setErr(''); return true;
  };

  const next = () => { if (validateStep()) setStep(s => Math.min(s+1, STEPS.length-1)); };
  const prev = () => { setErr(''); setStep(s => Math.max(s-1,0)); };

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const r = await API.curriculos.criar({ ...form, cpf: form.cpf.replace(/\D/g,'') });
      /* A candidatura já está gravada mesmo se um dos anexos falhar aqui —
         melhor ter o currículo sem foto/CV do que perder tudo por causa de
         rede instável no upload. */
      if (foto) {
        try { await API.curriculos.anexarFoto(r.id, foto); }
        catch (e) { setErr('Currículo enviado, mas a foto não foi anexada: ' + (e.message || 'erro desconhecido') + '.'); }
      }
      if (curriculoArq) {
        try { await API.curriculos.anexarCv(r.id, curriculoArq); }
        catch (e) { setErr(prev => (prev ? prev + ' ' : '') + 'O currículo em PDF não foi anexado: ' + (e.message || 'erro desconhecido') + '.'); }
      }
      onChange && onChange();
      setOk(true);
      setForm(empty); setFoto(null); setCurriculoArq(null); setStep(0);
    } catch (e) {
      setErr(e.message || 'Não foi possível enviar o currículo.');
    }
    setBusy(false);
  };

  const Row = ({label, val}) => val ? <div className="review-row"><span className="k">{label}</span><span className="v">{val}</span></div> : null;

  return (
    <div className="main-pad">
      <div className="page-title-row">
        <div className="page-title">
          <h2>Cadastro de Currículo</h2>
          <p>Preencha seus dados para se candidatar às vagas disponíveis no shopping</p>
        </div>
      </div>

      <div className="stepper-shell">
        <aside className="stepper-aside">
          <h3>Candidatura</h3>
          <h2>Seu Currículo</h2>
          <div className="step-list">
            {STEPS.map((s,i) => (
              <div key={i} className={`step-item ${i===step?'active':''} ${i<step?'done':''}`}>
                <div className="step-num">{i<step?<Icon name="check" size={12}/>:i+1}</div>
                <div className="step-label">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="footer-tip">Seus dados ficam protegidos por autorização no servidor. Apenas os recrutadores autorizados têm acesso.</div>
        </aside>

        <div className="stepper-main">
          {ok && (
            <div style={{ textAlign:'center',padding:40 }}>
              <div style={{ fontSize:56,marginBottom:12 }}>✅</div>
              <div style={{ fontSize:20,fontWeight:600,color:'var(--ok)' }}>Currículo enviado com sucesso!</div>
              <p style={{ fontSize:13,color:'var(--ink-3)',marginTop:8,lineHeight:1.6 }}>Seus dados foram armazenados com segurança. O lojista responsável pela vaga entrará em contato.</p>
              {err && <div style={{ color:'var(--err)',fontSize:12,padding:'8px 12px',background:'var(--err-bg)',borderRadius:8,marginTop:16,textAlign:'left' }}>{err}</div>}
              <button className="btn btn-ghost" style={{ marginTop:20 }} onClick={()=>{ setOk(false); setErr(''); }}>Enviar outro currículo</button>
            </div>
          )}

          {/* STEP 0 — Dados pessoais */}
          {!ok && step===0 && (
            <div>
              <h3>Dados pessoais e profissionais</h3>
              <p className="lead">Preencha seus dados de identificação.</p>
              <div className="field"><label>Nome completo <span className="req">*</span></label><input type="text" value={form.nome} onChange={e=>set('nome',e.target.value)} placeholder="Seu nome completo"/></div>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                <div className="field">
                <label>CPF <span className="req">*</span></label>
                <input type="text" value={form.cpf}
                  onChange={e => set('cpf', maskCpf(e.target.value))}
                  placeholder="000.000.000-00" maxLength={14}
                  style={{ borderColor: form.cpf.replace(/\D/g,'').length===11 ? (validarCPF(form.cpf.replace(/\D/g,'')) ? 'var(--ok)' : 'var(--err)') : undefined }}/>
                {form.cpf.replace(/\D/g,'').length===11 && !validarCPF(form.cpf.replace(/\D/g,'')) && (
                  <div className="hint" style={{color:'var(--err)'}}>⚠ CPF inválido. Verifique os dígitos.</div>
                )}
                {form.cpf.replace(/\D/g,'').length===11 && validarCPF(form.cpf.replace(/\D/g,'')) && (
                  <div className="hint" style={{color:'var(--ok)'}}>✓ CPF válido</div>
                )}
              </div>
                <div className="field"><label>Data de nascimento</label><input type="date" value={form.dataNascimento} onChange={e=>set('dataNascimento',e.target.value)}/></div>
                <div className="field"><label>Sexo <span className="req">*</span></label>
                  <select value={form.sexo} onChange={e=>set('sexo',e.target.value)}>
                    <option value="">— Selecione —</option>
                    <option>Masculino</option><option>Feminino</option><option>Outro</option>
                  </select>
                </div>
                <div className="field"><label>Estado civil</label>
                  <select value={form.estadoCivil} onChange={e=>set('estadoCivil',e.target.value)}>
                    <option value="">— Selecione —</option>
                    {ESTADO_CIVIL.map(e=><option key={e}>{e}</option>)}
                  </select>
                </div>
                <div className="field"><label>Nacionalidade</label>
                  <select value={form.nacionalidade} onChange={e=>set('nacionalidade',e.target.value)}>
                    <option>Brasileira</option><option>Estrangeira</option>
                  </select>
                </div>
                <div className="field"><label>Escolaridade <span className="req">*</span></label>
                  <select value={form.escolaridade} onChange={e=>set('escolaridade',e.target.value)}>
                    <option value="">— Selecione —</option>
                    {ESCOLARIDADES.map(e=><option key={e}>{e}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
                <div className="field">
                  <label>Foto (JPG ou PNG)</label>
                  <input type="file" accept="image/jpeg,image/jpg,image/png" onChange={e=>setFoto(e.target.files[0]||null)} style={{ height:'auto',padding:'8px 10px' }}/>
                  {foto && <div className="hint">✅ {foto.name}</div>}
                </div>
                <div className="field">
                  <label>Currículo (PDF)</label>
                  <input type="file" accept="application/pdf" onChange={e=>setCurriculoArq(e.target.files[0]||null)} style={{ height:'auto',padding:'8px 10px' }}/>
                  {curriculoArq && <div className="hint">✅ {curriculoArq.name}</div>}
                </div>
              </div>
            </div>
          )}

          {/* STEP 1 — Endereço */}
          {!ok && step===1 && (
            <StepEndereco form={form} set={set}/>
          )}

          {/* STEP 2 — Contato */}
          {!ok && step===2 && (
            <StepContato form={form} set={set}/>
          )}

          {/* STEP 3 — Histórico & Cargos */}
          {!ok && step===3 && (
            <div>
              <h3>Histórico, qualificação e objetivo profissional</h3>
              <p className="lead">Conte um pouco sobre você e os cargos que pretende.</p>
              <div className="field">
                <label>Escreva aqui um pouco mais sobre você</label>
                <textarea rows={5} value={form.historico} onChange={e=>set('historico',e.target.value)} placeholder="Experiências anteriores, habilidades, objetivos profissionais..."/>
              </div>
              <div className="divider"/>
              <div className="label-eyebrow">Cargos pretendidos</div>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginTop:10 }}>
                {[1,2,3,4].map(n => (
                  <div className="field" key={n}>
                    <label>Cargo {n}{n===1?<span className="req">*</span>:''}</label>
                    <select value={form[`cargo${n}`]} onChange={e=>set(`cargo${n}`,e.target.value)}>
                      <option value="">— Selecione —</option>
                      {CARGOS_CURRICULO.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 4 — Revisão */}
          {!ok && step===4 && (
            <div>
              <h3>Revisão final</h3>
              <p className="lead">Confirme seus dados antes de enviar.</p>
              <div style={{ background:'var(--surface-2)',borderRadius:10,padding:16,marginBottom:16 }}>
                <div className="label-eyebrow" style={{ marginBottom:10 }}>Dados pessoais</div>
                <Row label="Nome"          val={form.nome}/>
                <Row label="CPF"           val={form.cpf}/>
                <Row label="Nascimento"    val={form.dataNascimento}/>
                <Row label="Sexo"          val={form.sexo}/>
                <Row label="Estado civil"  val={form.estadoCivil}/>
                <Row label="Nacionalidade" val={form.nacionalidade}/>
                <Row label="Escolaridade"  val={form.escolaridade}/>
                <Row label="Foto"          val={foto?'✅ Anexada':'—'}/>
                <Row label="Currículo PDF" val={curriculoArq?'✅ Anexado':'—'}/>
              </div>
              <div style={{ background:'var(--surface-2)',borderRadius:10,padding:16,marginBottom:16 }}>
                <div className="label-eyebrow" style={{ marginBottom:10 }}>Endereço</div>
                <Row label="Endereço" val={[form.endereco,form.numero,form.complemento].filter(Boolean).join(', ')}/>
                <Row label="Bairro"   val={form.bairro}/>
                <Row label="Cidade"   val={[form.cidade,form.estado].filter(Boolean).join(' — ')}/>
                <Row label="CEP"      val={form.cep}/>
              </div>
              <div style={{ background:'var(--surface-2)',borderRadius:10,padding:16,marginBottom:16 }}>
                <div className="label-eyebrow" style={{ marginBottom:10 }}>Contato</div>
                <Row label="E-mail"    val={form.email}/>
                <Row label="Celular"   val={form.celular}/>
                <Row label="Instagram" val={form.instagram}/>
                <Row label="LinkedIn"  val={form.linkedin}/>
                <Row label="Facebook"  val={form.facebook}/>
              </div>
              <div style={{ background:'var(--surface-2)',borderRadius:10,padding:16,marginBottom:16 }}>
                <div className="label-eyebrow" style={{ marginBottom:10 }}>Cargos pretendidos</div>
                {[1,2,3,4].map(n => <Row key={n} label={`Cargo ${n}`} val={form[`cargo${n}`]}/>)}
              </div>
              <div style={{ fontSize:11,color:'var(--ink-3)',display:'flex',alignItems:'center',gap:6,padding:'10px 0' }}>
                <Icon name="shield" size={12}/> Seus dados ficam protegidos por autorização no servidor — só a equipe de RH acessa o banco de currículos.
              </div>
            </div>
          )}

          {err && !ok && <div style={{ color:'var(--err)',fontSize:12,padding:'8px 12px',background:'var(--err-bg)',borderRadius:8,marginTop:12 }}>{err}</div>}

          {!ok && (
            <div className="stepper-foot">
              <button className="btn btn-ghost" onClick={step===0?()=>{}:prev}>
                <Icon name="arrow-left" size={13}/> {step===0?'Cancelar':'Voltar'}
              </button>
              {step < STEPS.length-1
                ? <button className="btn btn-primary" onClick={next}>Continuar <Icon name="arrow-right" size={13}/></button>
                : <button className="btn btn-gold" onClick={submit} disabled={busy}>
                    <Icon name="send" size={13}/> {busy?'Enviando...':'Enviar currículo'}
                  </button>
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
